import {gql} from '@apollo/client';
import {
    PartnerPenaltyDetail,
    PartnerPenaltyFiltersTypeInput,
    PartnerPenaltyOperationLog,
    PartnerPenaltyPage,
    PartnerPenaltyStatusCounts,
    WarehousePenaltyFiltersTypeInput,
} from './types';

const PARTNER_PENALTY_LIST_FIELDS = `
    id
    penaltyNo
    creationTime
    penaltySourceDesc
    trackingNumber
    amount
    driverId
    driverName
    appealDeadlineAt
    status
`;

export const GET_PARTNER_PENALTY_LIST = gql`
    query PartnerPenaltyPageList(
        $filters: PartnerPenaltyFiltersTypeInput,
        $page: Int!,
        $size: Int!
    ) {
        partnerPenaltyPageList(filters: $filters, page: $page, size: $size) {
            content {
                ${PARTNER_PENALTY_LIST_FIELDS}
            }
            totalElements
            page
            size
        }
    }
`;

export interface GetPartnerPenaltyListResponse {
    partnerPenaltyPageList: PartnerPenaltyPage;
}

export interface GetPartnerPenaltyListVariables {
    filters: PartnerPenaltyFiltersTypeInput;
    page: number;
    size: number;
}

/**
 * 仓库侧列表。用 alias 把响应 key 对齐到 DSP 侧，
 * 使得 hooks / 响应类型可原样复用，调用方只需换 document。
 */
export const GET_WAREHOUSE_PENALTY_LIST = gql`
    query WarehousePenaltyPageList(
        $filters: WarehousePenaltyFiltersTypeInput!,
        $page: Int!,
        $size: Int!
    ) {
        partnerPenaltyPageList: warehousePenaltyPageList(filters: $filters, page: $page, size: $size) {
            content {
                ${PARTNER_PENALTY_LIST_FIELDS}
                dspName
            }
            totalElements
            page
            size
        }
    }
`;

export interface GetWarehousePenaltyListVariables {
    filters: WarehousePenaltyFiltersTypeInput;
    page: number;
    size: number;
}

/**
 * 罚单详情：详情页与申诉抽屉共用。
 * 后端从登录上下文取 dspId 并比对罚单归属，查他人罚单直接报错。
 */
export const GET_PARTNER_PENALTY_DETAIL = gql`
    query PartnerPenaltyDetail($id: Long!) {
        partnerPenaltyDetail(id: $id) {
            id
            penaltyNo
            creationTime
            penaltySourceDesc
            trackingNumber
            orderId
            routeSegmentCode
            amount
            driverId
            driverName
            appealDeadlineAt
            appealTime
            status
            appealReason
            evidenceObjectKeys
            evidenceImageUrls
            rejectReason
            operationLogs {
                id
                operatorName
                operatedAt
                description
            }
        }
    }
`;

export interface GetPartnerPenaltyDetailResponse {
    partnerPenaltyDetail: PartnerPenaltyDetail;
}

export interface GetPartnerPenaltyDetailVariables {
    id: number;
}

/** 仓库侧详情：warehouseId 必传，后端据此校验仓库权限与罚单归属 */
export const GET_WAREHOUSE_PENALTY_DETAIL = gql`
    query WarehousePenaltyDetail($id: Long!, $warehouseId: Long!) {
        partnerPenaltyDetail: warehousePenaltyDetail(id: $id, warehouseId: $warehouseId) {
            id
            penaltyNo
            creationTime
            penaltySourceDesc
            trackingNumber
            orderId
            routeSegmentCode
            amount
            driverId
            driverName
            dspName
            appealDeadlineAt
            appealTime
            status
            appealReason
            evidenceObjectKeys
            evidenceImageUrls
            rejectReason
            operationLogs {
                id
                operatorName
                operatedAt
                description
            }
        }
    }
`;

export interface GetWarehousePenaltyDetailVariables {
    id: number;
    warehouseId: number;
}

/** 全部 Tab 的计数一次拿齐；filters 不含 statuses（后端忽略该字段） */
export const GET_PARTNER_PENALTY_STATUS_COUNTS = gql`
    query PartnerPenaltyStatusCounts($filters: PartnerPenaltyFiltersTypeInput!) {
        partnerPenaltyStatusCounts(filters: $filters) {
            pendingSubmit
            reviewing
            approved
            rejected
            waiting
            timeout
        }
    }
`;

export interface GetPartnerPenaltyStatusCountsResponse {
    partnerPenaltyStatusCounts: PartnerPenaltyStatusCounts;
}

export interface GetPartnerPenaltyStatusCountsVariables {
    filters: PartnerPenaltyFiltersTypeInput;
}

export const GET_WAREHOUSE_PENALTY_STATUS_COUNTS = gql`
    query WarehousePenaltyStatusCounts($filters: WarehousePenaltyFiltersTypeInput!) {
        partnerPenaltyStatusCounts: warehousePenaltyStatusCounts(filters: $filters) {
            pendingSubmit
            reviewing
            approved
            rejected
            waiting
            timeout
        }
    }
`;

export interface GetWarehousePenaltyStatusCountsVariables {
    filters: WarehousePenaltyFiltersTypeInput;
}

/** 操作记录按罚单 ID 查（后端要用 id 做 DSP 归属校验，单号不足以定位） */
export const GET_PARTNER_PENALTY_OPERATION_LOGS = gql`
    query PartnerPenaltyOperationLogs($id: Long!) {
        partnerPenaltyOperationLogs(id: $id) {
            id
            operatedAt
            operatorName
            description
        }
    }
`;

export interface GetPartnerPenaltyOperationLogsResponse {
    partnerPenaltyOperationLogs: PartnerPenaltyOperationLog[];
}

export interface GetPartnerPenaltyOperationLogsVariables {
    id: number;
}
